import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  FileText,
  Lock,
  Eye,
  UserCheck,
  ChevronDown,
  Truck,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Mail,
  Phone,
  MapPin,
  Database,
  Share2,
  Trash2,
  Edit3,
  HelpCircle,
  ArrowRight,
  Sparkles,
  Menu,
  X,
  Globe,
  Clock,
  Server,
  Cookie,
  Fingerprint,
  Bell,
  CreditCard,
  HardDrive
} from 'lucide-react';

export default function PoliticaPrivacidade() {
  const [expandedSection, setExpandedSection] = useState('intro');
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSection = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sections = [
    {
      id: 'intro',
      title: "Nosso Compromisso com Você",
      icon: <Shield className="w-6 h-6" />,
      color: "brand",
      content: `A BoraFrete é uma plataforma de tecnologia que conecta embarcadores e transportadoras, facilitando a negociação e execução de fretes de forma segura, transparente e inteligente.

Esta Política de Privacidade rege o tratamento dos dados pessoais que usuários (empresas embarcadoras, transportadoras, motoristas autônomos e demais usuários) disponibilizam à BoraFrete durante a utilização de nossos serviços.

Nós, da BoraFrete, reiteramos o nosso compromisso com você e nossa preocupação com a sua privacidade e com a proteção de seus dados pessoais, em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018).

Ao utilizar nossos serviços, você manifesta estar ciente e de acordo com os termos desta Política de Privacidade.`
    },
    {
      id: 'coleta',
      title: "1. Dados que Coletamos",
      icon: <Database className="w-6 h-6" />,
      color: "blue",
      intro: "Coletamos apenas os dados necessários para prestar nossos serviços com excelência:",
      subsections: [
        {
          title: "Dados Cadastrais",
          icon: <Fingerprint className="w-5 h-5" />,
          items: ["Nome completo e nome social", "E-mail corporativo e pessoal", "Telefone celular e fixo", "CNPJ ou CPF", "Endereço completo (CEP, rua, número)", "Dados bancários (para pagamentos)", "RG e órgão emissor", "Data de nascimento", "CNH (categoria e validade)", "Selfie para verificação de identidade", "Dados de representantes legais"]
        },
        {
          title: "Dados do Veículo & Equipamento",
          icon: <Truck className="w-5 h-5" />,
          items: ["Placa, RNTRC e CRLV", "Marca, modelo e ano do veículo", "Tipo de carroceria", "Capacidade de carga (toneladas)", "Comprimento e largura útil", "Rastreador instalado (sim/não)", "Tipo de carga aceita", "Fotos do veículo e documentos"]
        },
        {
          title: "Dados de Navegação & Localização",
          icon: <Globe className="w-5 h-5" />,
          items: ["Endereço IP e provedor", "Geolocalização aproximada", "Dados do dispositivo (modelo, SO)", "Histórico de acesso e cliques", "Cookies e identificadores únicos", "Latitude/longitude em tempo real (com permissão)", "Rotas percorridas durante fretes", "Tempo de permanência em locais"]
        },
        {
          title: "Dados da Carga & Transação",
          icon: <HardDrive className="w-5 h-5" />,
          items: ["Tipo de produto transportado", "Origem e destino (endereços completos)", "Peso, dimensões e quantidade", "Valor da mercadoria (seguro)", "Notas fiscais e CTRC", "Valor do frete negociado", "Histórico de propostas e contratos", "Avaliações e feedbacks"]
        }
      ]
    },
    {
      id: 'uso',
      title: "2. Como Usamos seus Dados",
      icon: <Eye className="w-6 h-6" />,
      color: "purple",
      intro: "Seus dados são utilizados para as seguintes finalidades específicas:",
      items: [
        "Realizar cadastro, verificação de identidade e análise de crédito",
        "Viabilizar a conexão inteligente entre embarcadores e transportadoras",
        "Garantir segurança das transações, prevenir fraudes e calotes",
        "Rastreamento e monitoramento de cargas em tempo real",
        "Processamento de pagamentos, repasses e transações financeiras",
        "Avaliação de perfil, pontuação de motoristas e sistema de reputação",
        "Atendimento ao cliente, suporte técnico e resolução de conflitos",
        "Cumprir obrigações legais, fiscais e regulatórias (ANTT, Receita Federal)",
        "Geração de estatísticas anônimas e melhoria contínua dos serviços",
        "Envio de comunicações sobre fretes, propostas e atualizações da plataforma",
        "Marketing direcionado e novidades (com consentimento prévio e revogável)",
        "Investigação de incidentes de segurança e cumprimento de ordens judiciais"
      ]
    },
    {
      id: 'compartilhamento',
      title: "3. Compartilhamento de Dados",
      icon: <Share2 className="w-6 h-6" />,
      color: "amber",
      content: "Seus dados são compartilhados apenas quando estritamente necessário para a prestação dos serviços ou por obrigação legal:",
      items: [
        "Entre embarcadores e transportadoras para fechamento de fretes e execução do contrato de transporte",
        "Com parceiros de tecnologia (AWS, gateways de pagamento, serviços de SMS/email) sob contrato de confidencialidade",
        "Com autoridades públicas quando exigido por lei, ordem judicial ou regulador (ANTT, Polícia Federal, Receita)",
        "Com seguradoras, em caso de sinistro ou solicitação de seguro de carga",
        "Com gerenciadores de risco e análise de crédito para avaliação de perfil (quando aplicável)",
        "Em caso de fusão, aquisição ou venda de ativos da empresa, com garantia de proteção dos dados",
        "Com defensores públicos ou advogados, em caso de litígio judicial para defesa dos direitos da BoraFrete"
      ],
      alert: "Nunca vendemos seus dados para terceiros para fins de marketing não autorizado. O compartilhamento é sempre limitado ao mínimo necessário."
    },
    {
      id: 'seguranca',
      title: "4. Segurança da Informação",
      icon: <Lock className="w-6 h-6" />,
      color: "emerald",
      content: `Adotamos medidas técnicas e administrativas rigorosas, alinhadas às melhores práticas do mercado e padrões internacionais:

• Criptografia SSL/TLS em todas as transmissões de dados (AES-256)
• Armazenamento em servidores seguros na AWS (cloud computing), com certificações ISO 27001 e SOC 2
• Monitoramento contínuo 24/7 contra acessos não autorizados, invasões e anomalias
• Backups automatizados, criptografados e redundância geográfica de dados
• Autenticação de dois fatores (2FA) disponível para todos os usuários
• Firewall de última geração e proteção contra DDoS
• Treinamento contínuo da equipe em boas práticas de segurança e LGPD
• Auditorias internas e testes de invasão (pentest) periódicos
• Controle rigoroso de acesso baseado em princípio do menor privilégio

Nenhum sistema é 100% invulnerável. Em caso de incidente de segurança que possa afetar seus dados, você será notificado em até 72 horas, conforme exigido pela LGPD, e adotaremos todas as medidas necessárias para mitigar danos.`
    },
    {
      id: 'lgpd',
      title: "5. Seus Direitos (LGPD)",
      icon: <UserCheck className="w-6 h-6" />,
      color: "cyan",
      intro: "Como titular dos dados, a Lei Geral de Proteção de Dados garante a você os seguintes direitos:",
      grid: [
        { icon: <Eye className="w-5 h-5" />, title: "Acesso", desc: "Saber quais dados pessoais temos sobre você e como são tratados" },
        { icon: <Edit3 className="w-5 h-5" />, title: "Correção", desc: "Corrigir dados incompletos, inexatos ou desatualizados" },
        { icon: <Trash2 className="w-5 h-5" />, title: "Exclusão", desc: "Solicitar a eliminação dos seus dados (direito ao esquecimento)" },
        { icon: <Share2 className="w-5 h-5" />, title: "Portabilidade", desc: "Receber seus dados em formato estruturado e legível" },
        { icon: <AlertTriangle className="w-5 h-5" />, title: "Oposição", desc: "Discordar de determinados tratamentos e revogar consentimento" },
        { icon: <HelpCircle className="w-5 h-5" />, title: "Informação", desc: "Saber como, por que e por quanto tempo usamos seus dados" },
        { icon: <Clock className="w-5 h-5" />, title: "Revisão", desc: "Revisar decisões automatizadas (matching de cargas, perfil de risco)" },
        { icon: <CheckCircle className="w-5 h-5" />, title: "Revogação", desc: "Revogar consentimento a qualquer momento, de forma fácil e gratuita" }
      ],
      content: `Para exercer seus direitos, entre em contato com nosso Encarregado de Dados (DPO) através dos canais disponibilizados no final desta página. Responderemos em até 15 dias úteis, conforme previsto na LGPD.`
    },
    {
      id: 'retencao',
      title: "6. Retenção e Exclusão",
      icon: <Clock className="w-6 h-6" />,
      color: "slate",
      content: `Mantemos seus dados apenas pelo tempo estritamente necessário para cumprir as finalidades descritas nesta política ou exigido por lei:

• Dados cadastrais: enquanto sua conta estiver ativa ou até solicitação de exclusão
• Dados de fretes e transações: 5 anos (obrigação fiscal, contábil e legal - Código Civil/CDC)
• Dados de navegação e logs: até 12 meses (anonimizados após este período)
• Dados de localização em tempo real: 90 dias após finalização do frete
• Backups e cópias de segurança: até 30 dias após exclusão solicitada
• Dados de verificação de identidade (selfie): até 2 anos após encerramento da conta

Você pode solicitar a exclusão da conta e dados a qualquer momento através do e-mail privacidade@borafrete.com.br. Alguns dados poderão ser retidos de forma anonimizada ou pseudonimizada para cumprimento de obrigações legais, estatísticas ou defesa de direitos.`
    },
    {
      id: 'cookies',
      title: "7. Cookies e Tecnologias",
      icon: <Cookie className="w-6 h-6" />,
      color: "orange",
      content: "Utilizamos cookies, pixels, web beacons e tecnologias similares para melhorar sua experiência, segurança e funcionalidade da plataforma:",
      items: [
        "Essenciais: Autenticação, login persistente, segurança da sessão, prevenção de fraudes. Sem estes, a plataforma não funciona corretamente.",
        "Analíticos: Google Analytics, Mixpanel - entender como você usa nossos serviços, páginas mais visitadas, melhorias de UX (dados anonimizados).",
        "Preferências: Idioma, região, tipo de veículo padrão, filtros de busca salvos, personalização da interface.",
        "Marketing: Facebook Pixel, Google Ads - personalizar anúncios e comunicações (apenas com seu consentimento explícito).",
        "Funcionais: Chat de suporte, notificações push, lembretes de fretes similares."
      ],
      alert: "Você pode gerenciar suas preferências de cookies a qualquer momento através do banner de cookies na primeira visita ou nas configurações da conta. Desativar cookies essenciais pode comprometer o funcionamento da plataforma."
    },
    {
      id: 'transferencia',
      title: "8. Transferência Internacional",
      icon: <Globe className="w-6 h-6" />,
      color: "indigo",
      content: `Utilizamos serviços de cloud computing (Amazon Web Services - AWS) que podem envolver transferência internacional de dados para os Estados Unidos e outros países com adequado nível de proteção.

Todas as transferências internacionais de dados pessoais são realizadas:
• Com cláusulas contratuais padrão de proteção de dados (SCCs) aprovadas pela Comissão Europeia e ANPD
• Para países com nível adequado de proteção reconhecido pela ANPD (EUA, mediante certificações)
• Com garantias de segurança, confidencialidade e direitos dos titulares
• Com sub-processadores que aderem ao Código de Conduta da Câmara de Comércio Europeia-Suíça e Privacy Shield (quando aplicável)

Você pode solicitar cópia das cláusulas contratuais e mais informações sobre transferências internacionais entrando em contato com nosso encarregado de dados.`
    },
    {
      id: 'atualizacoes',
      title: "9. Atualizações desta Política",
      icon: <Bell className="w-6 h-6" />,
      color: "pink",
      content: `Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nossos serviços, tecnologias, parceiros ou na legislação aplicável.

• Alterações significativas (novos usos de dados, novos parceiros): notificação por e-mail com 30 dias de antecedência
• Alterações menores (ajustes de redação, clareza): aviso na plataforma com 15 dias de antecedência
• Alterações urgentes (segurança, obrigação legal): notificação imediata
• Continuidade de uso após atualização implica aceitação dos novos termos

Recomendamos revisar esta política periodicamente. A data da última atualização está sempre indicada no topo do documento.`
    },
    {
      id: 'responsabilidade',
      title: "10. Responsabilidades do Usuário",
      icon: <CreditCard className="w-6 h-6" />,
      color: "rose",
      content: `O usuário também possui responsabilidades importantes na proteção de dados:

• Manter suas credenciais de acesso (senha, 2FA) em sigilo absoluto
• Não compartilhar sua conta com terceiros (proibido o uso por pessoas não cadastradas)
• Garantir a veracidade e atualização dos dados cadastrais fornecidos
• Respeitar a privacidade de outros usuários (não coletar dados de terceiros sem autorização)
• Notificar imediatamente a BoraFrete em caso de suspeita de acesso não autorizado à conta
• Utilizar dispositivos seguros, com antivírus atualizado e sistemas operacionais atualizados

A BoraFrete não se responsabiliza por vazamentos de dados causados por negligência do usuário no cumprimento destas obrigações.`
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      brand: "from-brand-500/20 to-purple-500/20 border-brand-500/30 text-brand-400",
      blue: "from-blue-500/20 to-cyan-500/20 border-blue-500/30 text-blue-400",
      purple: "from-purple-500/20 to-pink-500/20 border-purple-500/30 text-purple-400",
      amber: "from-amber-500/20 to-orange-500/20 border-amber-500/30 text-amber-400",
      emerald: "from-emerald-500/20 to-teal-500/20 border-emerald-500/30 text-emerald-400",
      cyan: "from-cyan-500/20 to-blue-500/20 border-cyan-500/30 text-cyan-400",
      slate: "from-slate-500/20 to-gray-500/20 border-slate-500/30 text-slate-400",
      orange: "from-orange-500/20 to-amber-500/20 border-orange-500/30 text-orange-400",
      indigo: "from-indigo-500/20 to-purple-500/20 border-indigo-500/30 text-indigo-400",
      pink: "from-pink-500/20 to-rose-500/20 border-pink-500/30 text-pink-400",
      rose: "from-rose-500/20 to-red-500/20 border-rose-500/30 text-rose-400"
    };
    return colors[color] || colors.brand;
  };

  return (
    <div className="min-h-screen bg-[#050508] text-slate-200 font-sans antialiased overflow-x-hidden selection:bg-brand-500/30 selection:text-brand-200">
      
      {/* Dynamic Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_20%,rgba(59,130,246,0.15)_0%,transparent_50%)] animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_80%,rgba(139,92,246,0.15)_0%,transparent_50%)] animate-pulse" style={{ animationDuration: '10s' }} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(6,182,212,0.05)_0%,transparent_70%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Header */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: scrollY > 50 ? 'rgba(5, 5, 8, 0.95)' : 'transparent',
          backdropFilter: scrollY > 50 ? 'blur(20px)' : 'none',
          borderBottom: scrollY > 50 ? '1px solid rgba(255,255,255,0.1)' : 'none'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-brand-500/50 transition-all duration-300 group-hover:scale-110">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <div className="absolute inset-0 bg-brand-500 rounded-xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-300" />
              </div>
              <div className="hidden sm:block">
                <span className="text-xl font-bold text-white tracking-tight">
                  Bora<span className="text-brand-400">Frete</span>
                </span>
                <span className="block text-[10px] text-slate-500 uppercase tracking-widest">Política de Privacidade</span>
              </div>
            </Link>
            
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span>LGPD Compliant</span>
              </div>
              
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              
              <Link 
                to="/"
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-sm font-medium"
              >
                <span>Voltar ao app</span>
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-[#050508]/95 backdrop-blur-xl border-b border-white/10 p-4">
            <div className="flex flex-col gap-2">
              <Link to="/" className="flex items-center gap-2 px-4 py-3 rounded-lg bg-white/5 text-white">
                <Truck className="w-5 h-5" />
                <span>Voltar ao BoraFrete</span>
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur border border-white/10 mb-8 text-sm text-brand-400 shadow-2xl animate-[float_6s_ease-in-out_infinite]">
            <Shield className="w-4 h-4" />
            <span>Proteção de Dados & Privacidade</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-brand-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
              Política de Privacidade
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Transparência total sobre como coletamos, usamos e protegemos seus dados 
            enquanto você move o Brasil conosco. Sua privacidade é nossa prioridade.
          </p>

          <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:border-emerald-500/30 transition-colors">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              Lei 13.709/2018 (LGPD)
            </span>
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:border-brand-500/30 transition-colors">
              <Clock className="w-4 h-4 text-brand-400" />
              Atualizado: 24/03/2026
            </span>
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:border-purple-500/30 transition-colors">
              <Sparkles className="w-4 h-4 text-purple-400" />
              Versão 2.0
            </span>
          </div>
        </div>
      </section>

      {/* Progress Indicator */}
      <div className="sticky top-16 z-40 bg-[#050508]/80 backdrop-blur-sm border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Progresso de leitura</span>
            <span>{Math.min(100, Math.round((scrollY / (document.body.scrollHeight - window.innerHeight)) * 100))}%</span>
          </div>
          <div className="mt-1 h-1 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-brand-500 to-purple-500 transition-all duration-300"
              style={{ width: `${Math.min(100, (scrollY / (document.body.scrollHeight - window.innerHeight)) * 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-4">
        
        {sections.map((section, index) => (
          <article 
            key={section.id}
            className={`group relative overflow-hidden rounded-2xl border backdrop-blur-xl transition-all duration-500 hover:scale-[1.01] bg-gradient-to-br ${getColorClasses(section.color)} ${expandedSection === section.id ? 'bg-opacity-30 shadow-2xl shadow-brand-500/10' : 'bg-opacity-10'}`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className={`absolute inset-0 bg-gradient-to-r ${getColorClasses(section.color).split(' ')[0]} opacity-0 group-hover:opacity-10 transition-opacity duration-700`} />
            
            <button 
              onClick={() => toggleSection(section.id)}
              className="w-full p-6 sm:p-8 flex items-center justify-between text-left relative z-10"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getColorClasses(section.color)} bg-opacity-30 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  {React.cloneElement(section.icon, { className: "w-6 h-6 text-white" })}
                </div>
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Seção {index + 1}</span>
                  <h2 className="text-xl font-bold text-white group-hover:text-brand-200 transition-colors">
                    {section.title}
                  </h2>
                </div>
              </div>
              <div className={`w-10 h-10 rounded-full bg-white/5 flex items-center justify-center transition-all duration-300 ${expandedSection === section.id ? 'rotate-180 bg-white/10' : 'group-hover:bg-white/10'}`}>
                <ChevronDown className="w-5 h-5 text-slate-400" />
              </div>
            </button>

            <div className={`overflow-hidden transition-all duration-700 ${expandedSection === section.id ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="px-6 pb-6 sm:px-8 sm:pb-8 pt-2 space-y-6">
                
                {section.intro && (
                  <p className="text-slate-300 leading-relaxed text-lg border-l-4 border-brand-500/50 pl-4">
                    {section.intro}
                  </p>
                )}

                {section.content && (
                  <div className="text-slate-300 leading-relaxed whitespace-pre-line text-sm sm:text-base">
                    {section.content}
                  </div>
                )}

                {section.subsections && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {section.subsections.map((sub, i) => (
                      <div key={i} className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-brand-500/30 hover:bg-white/10 transition-all duration-300 group/sub">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 rounded-lg bg-brand-500/20 flex items-center justify-center text-brand-400 group-hover/sub:scale-110 transition-transform">
                            {sub.icon}
                          </div>
                          <h4 className="text-white font-semibold">{sub.title}</h4>
                        </div>
                        <ul className="space-y-2">
                          {sub.items.map((item, j) => (
                            <li key={j} className="flex items-start gap-2 text-slate-400 text-sm">
                              <span className="w-1.5 h-1.5 rounded-full bg-brand-400 mt-2 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {section.items && !section.grid && (
                  <ul className="space-y-3">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-emerald-500/30 hover:bg-white/10 transition-all duration-300 group/item">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" />
                        <span className="text-slate-300 text-sm leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.grid && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {section.grid.map((item, i) => (
                      <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10 text-center hover:bg-white/10 hover:border-brand-500/30 transition-all duration-300 group/item cursor-pointer">
                        <div className="text-brand-400 mb-3 group-hover/item:scale-110 transition-transform flex justify-center">
                          {item.icon}
                        </div>
                        <h4 className="text-white font-semibold text-sm mb-1">{item.title}</h4>
                        <p className="text-slate-400 text-xs leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                {section.alert && (
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm flex items-start gap-3 animate-pulse" style={{ animationDuration: '3s' }}>
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">{section.alert}</span>
                  </div>
                )}

              </div>
            </div>
          </article>
        ))}

        {/* Contact Card */}
        <div className="mt-16 p-8 rounded-3xl bg-gradient-to-br from-brand-500/20 via-purple-500/20 to-blue-500/20 border border-brand-500/40 backdrop-blur-xl relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-500/0 via-white/10 to-brand-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
          
          <div className="relative z-10 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
              <Sparkles className="w-4 h-4 text-brand-400" />
              <span className="text-sm text-white font-medium">DPO - Encarregado de Dados</span>
            </div>
            
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Dúvidas sobre privacidade?</h3>
            <p className="text-slate-300 mb-8 max-w-lg mx-auto leading-relaxed">
              Nosso encarregado de proteção de dados (DPO) está à disposição para esclarecimentos, 
              solicitações de direitos LGPD e questões relacionadas à privacidade.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="mailto:privacidade@borafrete.com.br"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-brand-500/50 rounded-xl transition-all text-white font-semibold group/email shadow-lg hover:shadow-brand-500/25"
              >
                <Mail className="w-5 h-5 group-hover/email:scale-110 transition-transform" />
                <span>privacidade@borafrete.com.br</span>
              </a>
              
              <a 
                href="tel:+5565981181997"
                className="inline-flex items-center gap-3 px-8 py-4 bg-brand-500/20 hover:bg-brand-500/30 border border-brand-500/30 hover:border-brand-500/50 rounded-xl transition-all text-brand-300 font-semibold shadow-lg hover:shadow-brand-500/25"
              >
                <Phone className="w-5 h-5" />
                <span>(65) 98118-1997</span>
              </a>
            </div>
            
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                <MapPin className="w-4 h-4" />
                <span>Cuiabá/MT • CNPJ: 65.852.223/0001-22</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                <Clock className="w-4 h-4" />
                <span>Atendimento: Seg-Sex, 8h-18h</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          <Link 
            to="/termo-responsabilidade"
            className="flex items-center justify-between p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-brand-500/30 hover:bg-white/10 transition-all duration-300 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <FileText className="w-5 h-5 text-brand-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm">Termo de Responsabilidade</h4>
                <p className="text-slate-500 text-xs mt-1">Obrigações das partes</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-brand-400 group-hover:translate-x-1 transition-all" />
          </Link>
          
          <Link 
            to="/termo-seguranca-carga"
            className="flex items-center justify-between p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-emerald-500/30 hover:bg-white/10 transition-all duration-300 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Shield className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm">Proteção de Carga</h4>
                <p className="text-slate-500 text-xs mt-1">Segurança da mercadoria</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all" />
          </Link>
          
          <Link 
            to="/termos-de-uso"
            className="flex items-center justify-between p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-blue-500/30 hover:bg-white/10 transition-all duration-300 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <FileText className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold text-sm">Termos de Uso</h4>
                <p className="text-slate-500 text-xs mt-1">Regras da plataforma</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
          </Link>
        </div>

      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-black/60 backdrop-blur-xl py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-white">Bora<span className="text-brand-400">Frete</span></span>
                <p className="text-xs text-slate-500">Tecnologia que move o Brasil 🇧🇷</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span>© 2026 BoraFrete</span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">Todos os direitos reservados</span>
            </div>
            
            <Link 
              to="/politica-privacidade"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-brand-500/10 border border-brand-500/30 text-brand-400 hover:bg-brand-500/20 transition-all text-sm font-medium"
            >
              <Shield className="w-4 h-4" />
              <span>Privacidade & Proteção</span>
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            </Link>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 rounded-full bg-brand-500/20 hover:bg-brand-500/30 border border-brand-500/30 text-brand-400 flex items-center justify-center backdrop-blur-xl transition-all duration-300 hover:scale-110 z-50 shadow-lg hover:shadow-brand-500/25"
        >
          <ChevronDown className="w-6 h-6 rotate-180" />
        </button>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #050508;
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.2);
        }
        
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}